import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';
import { asTimestampedValue } from '#plugin-chat/lib/timeseries/datapoint-value.ts';

/**
 * Number of leading values probed when classifying a column as a
 * datapoint-array column. Bounded so callers (chart render, transform
 * guards) stay cheap on wide datasets with many candidate columns.
 */
const DETECTION_PROBE_LIMIT = 8;

type Probe = { kind: 'unknown' } | { kind: 'arrays'; sawDatapoint: boolean };

/**
 * Core detection: probe up to {@link DETECTION_PROBE_LIMIT} non-null values
 * from a single column's worth of samples and decide whether they are CDF
 * datapoint arrays (`Array<{ timestamp, value | average | sum | count | min | max }>`).
 *
 * Decision rule:
 * - Any non-array, non-null value disqualifies the column.
 * - Any array whose first element parses as `{ timestamp, <numeric> }` confirms it.
 * - All-null and all-empty-array inputs return false (no signal to flatten).
 *
 * Used by both {@link isDatapointArrayColumn} (Dataset-level, chart render)
 * and the `spread`/`unroll` guards in the Arquero executor (table-level,
 * transform pipeline). Both call sites share the same probe behavior so
 * the LLM-facing semantics agree across layers.
 */
export function isDatapointArraySamples(values: readonly unknown[]): boolean {
  const samples = values.filter((v): v is unknown => v !== null && v !== undefined).slice(0, DETECTION_PROBE_LIMIT);

  const verdict = samples.reduce<Probe>(
    (acc, value) => {
      if (acc.kind === 'unknown') {
        return acc;
      }
      if (!Array.isArray(value)) {
        return { kind: 'unknown' };
      }
      if (acc.sawDatapoint) {
        return acc;
      }
      if (value.length === 0) {
        return acc;
      }
      return { kind: 'arrays', sawDatapoint: asTimestampedValue(value[0]) !== null };
    },
    { kind: 'arrays', sawDatapoint: false },
  );

  return verdict.kind === 'arrays' && verdict.sawDatapoint;
}

/**
 * Dataset-level wrapper around {@link isDatapointArraySamples}. Returns
 * `false` for unknown columns.
 */
export function isDatapointArrayColumn(dataset: Dataset, column: string): boolean {
  return isDatapointArraySamples(dataset.rows.map((row) => row[column]));
}

/**
 * Filter `candidateColumns` down to those that exist in `dataset` and
 * carry datapoint arrays. Returns columns in the input order.
 */
export function detectDatapointArrayColumns(dataset: Dataset, candidateColumns: readonly string[]): readonly string[] {
  const datasetColumnNames = new Set(dataset.columns.map((c) => c.name));
  return candidateColumns.filter((c) => datasetColumnNames.has(c) && isDatapointArrayColumn(dataset, c));
}
